import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { Icon } from '@/components/ui/icon';
import { CONTROL_SIZE } from '@/components/ui/input';
import { Popover } from '@/components/ui/popover';
import { useClickOutside, useEscapeKey } from '@/hooks';
import { cn } from '@/lib/cn';

export interface SelectOption {
  value: string;
  label: string;
  /** Optional secondary line under the label. */
  hint?: string;
  disabled?: boolean;
}

export interface SelectProps {
  options: SelectOption[];
  value: string | null;
  onChange: (value: string) => void;
  placeholder?: string;
  invalid?: boolean;
  disabled?: boolean;
  id?: string;
  name?: string;
  className?: string;
  'aria-describedby'?: string;
  /** Names the control when no visible <Field> label does - a filter bar. */
  'aria-label'?: string;
  /** Shows a clear affordance once a value is set. */
  clearable?: boolean;
}

/**
 * A listbox built from scratch - no native `<select>` anywhere in WinDocs.
 *
 * That means the keyboard contract has to be provided rather than inherited:
 * Up/Down move the active option, Home/End jump to either end, Enter or Space
 * commits, Escape closes, typing jumps to the next option starting with those
 * characters. `aria-activedescendant` keeps screen readers in step without
 * moving DOM focus off the trigger.
 */
export function Select({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  invalid = false,
  disabled = false,
  id,
  name,
  className,
  'aria-describedby': describedBy,
  'aria-label': ariaLabel,
  clearable = false,
}: SelectProps) {
  const generatedId = useId();
  const controlId = id ?? generatedId;
  const listboxId = `${controlId}-listbox`;

  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const typeaheadRef = useRef({ query: '', timer: 0 });

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const selected = options.find((option) => option.value === value) ?? null;
  const selectableIndexes = options
    .map((option, index) => (option.disabled ? -1 : index))
    .filter((index) => index >= 0);

  const close = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
  }, []);

  useClickOutside([triggerRef, listRef], close, open);
  useEscapeKey(() => {
    close();
    triggerRef.current?.focus();
  }, open);

  /**
   * Opening lands on the current value, or the first selectable option. Done
   * in the handler rather than an effect so opening costs one render.
   */
  function openList(): void {
    const currentIndex = options.findIndex((option) => option.value === value);
    setActiveIndex(currentIndex >= 0 ? currentIndex : (selectableIndexes[0] ?? -1));
    setOpen(true);
  }

  /* Keep the active option in view while arrowing through a long list. */
  useEffect(() => {
    if (!open || activeIndex < 0) {
      return;
    }

    listRef.current
      ?.querySelector(`[data-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  function moveActive(delta: number): void {
    if (selectableIndexes.length === 0) {
      return;
    }

    const currentPosition = selectableIndexes.indexOf(activeIndex);
    const nextPosition =
      currentPosition === -1
        ? 0
        : (currentPosition + delta + selectableIndexes.length) % selectableIndexes.length;

    setActiveIndex(selectableIndexes[nextPosition] ?? -1);
  }

  function commit(index: number): void {
    const option = options[index];

    if (!option || option.disabled) {
      return;
    }

    onChange(option.value);
    close();
    triggerRef.current?.focus();
  }

  /** Type-to-jump, the behaviour a native select gives for free. */
  function handleTypeahead(character: string): void {
    const state = typeaheadRef.current;

    window.clearTimeout(state.timer);
    state.query += character.toLowerCase();
    state.timer = window.setTimeout(() => {
      state.query = '';
    }, 600);

    const match = options.findIndex(
      (option) => !option.disabled && option.label.toLowerCase().startsWith(state.query),
    );

    if (match >= 0) {
      setActiveIndex(match);
      if (!open) {
        onChange(options[match]?.value ?? '');
      }
    }
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLButtonElement>): void {
    if (disabled) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (open) {
          moveActive(1);
        } else {
          openList();
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (open) {
          moveActive(-1);
        } else {
          openList();
        }
        break;
      case 'Home':
        if (open) {
          event.preventDefault();
          setActiveIndex(selectableIndexes[0] ?? -1);
        }
        break;
      case 'End':
        if (open) {
          event.preventDefault();
          setActiveIndex(selectableIndexes[selectableIndexes.length - 1] ?? -1);
        }
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (open) {
          commit(activeIndex);
        } else {
          openList();
        }
        break;
      case 'Tab':
        /* Tabbing away commits nothing and closes, like a native select. */
        close();
        break;
      default:
        if (event.key.length === 1 && !event.metaKey && !event.ctrlKey) {
          handleTypeahead(event.key);
        }
    }
  }

  return (
    <>
      {/* Mirrors the value for uncontrolled form reads and autofill. */}
      {name && <input type="hidden" name={name} value={value ?? ''} />}

      <button
        ref={triggerRef}
        id={controlId}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-invalid={invalid ? true : undefined}
        aria-describedby={describedBy}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => {
          if (open) {
            close();
          } else {
            openList();
          }
        }}
        onKeyDown={onKeyDown}
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-sm border bg-surface text-left',
          CONTROL_SIZE,
          'transition-colors duration-100',
          'disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-content-subtle',
          invalid ? 'border-danger' : 'border-border-strong hover:border-content-subtle',
          className,
        )}
      >
        <span className={cn('truncate', selected ? 'text-content' : 'text-content-subtle')}>
          {selected?.label ?? placeholder}
        </span>

        <span className="flex shrink-0 items-center gap-1">
          {clearable && selected && !disabled && (
            <span
              role="button"
              tabIndex={-1}
              aria-label="Clear selection"
              onClick={(event) => {
                event.stopPropagation();
                onChange('');
              }}
              className="text-content-subtle hover:text-content"
            >
              <Icon name="close" size={12} />
            </span>
          )}
          <Icon
            name="chevron-down"
            size={14}
            className={cn('text-content-subtle transition-transform', open && 'rotate-180')}
          />
        </span>
      </button>

      {/* Capped, now that the menu may grow past the trigger: an option list is
          allowed to be wider than the control it hangs off, but not arbitrarily
          - a long label should wrap the menu's width, not the window's. */}
      <Popover anchorRef={triggerRef} open={open} matchAnchorWidth className="max-w-80 py-1">
        <div ref={listRef} id={listboxId} role="listbox" className="max-h-60 overflow-y-auto">
          {options.length === 0 && (
            <p className="px-2 py-2 text-xs text-content-subtle">No options available</p>
          )}

          {options.map((option, index) => {
            const isSelected = option.value === value;
            const isActive = index === activeIndex;

            return (
              <div
                key={option.value}
                role="option"
                data-index={index}
                aria-selected={isSelected}
                aria-disabled={option.disabled}
                onClick={() => {
                  commit(index);
                }}
                onMouseEnter={() => {
                  if (!option.disabled) {
                    setActiveIndex(index);
                  }
                }}
                className={cn(
                  'flex cursor-pointer items-center justify-between gap-2 px-2 py-1.5 text-xs',
                  option.disabled && 'cursor-not-allowed text-content-subtle',
                  isActive && !option.disabled && 'bg-surface-muted',
                  isSelected && 'font-medium text-primary',
                )}
              >
                <span className="min-w-0">
                  <span className="block truncate">{option.label}</span>
                  {option.hint && (
                    <span className="block truncate text-2xs text-content-subtle">
                      {option.hint}
                    </span>
                  )}
                </span>
                {isSelected && <Icon name="check" size={14} className="shrink-0" />}
              </div>
            );
          })}
        </div>
      </Popover>
    </>
  );
}
